/**
 * URL sanitization for safe logging and observability.
 *
 * Router-level URL handlers and any code that ships URLs to logs, Sentry,
 * analytics, or breadcrumbs must pass URLs through {@link sanitizeUrl} first.
 * The function strips the values of known sensitive query parameters
 * AND known sensitive URL-fragment parameters (OAuth implicit-flow uses the
 * fragment for `access_token`, see RFC 6749 §4.2.2). Path, fragment
 * structure, benign params, and cross-origin host are preserved so logs
 * remain useful for forensics.
 *
 * Pure module — no Angular DI dependencies, safe to call from anywhere
 * including pre-bootstrap and unit tests.
 */

/**
 * Lowercased parameter names whose VALUES must be redacted, matched against
 * BOTH `URLSearchParams` (`?key=value`) AND fragment-encoded params
 * (`#key=value`, the OAuth implicit-flow / hybrid-flow convention).
 *
 * Comparison in {@link sanitizeUrl} is case-insensitive.
 *
 * Add to this list (not to call sites) when introducing a new credential
 * convention. Keeping the canonical list in one place guarantees redaction
 * is uniform across all consumers and across query/fragment surfaces.
 */
export const SENSITIVE_QUERY_PARAMS: ReadonlySet<string> = new Set([
  // Auth tokens and credentials
  'access_key',
  'access_token',
  'apikey',
  'api_key',
  'assertion',
  'auth',
  'auth_token',
  'authorization',
  'bearer',
  'code',
  'csrf',
  'csrf_token',
  'expires_in',
  'id_token',
  'invitation',
  'invite',
  'jwt',
  'key',
  'login_hint',
  'magic',
  'magic_link',
  'mfa',
  'nonce',
  'otp',
  'passwd',
  'password',
  'private_key',
  'pwd',
  'refresh_token',
  'reset',
  'reset_token',
  'saml',
  'secret',
  'secret_key',
  'session',
  'session_id',
  'sessionid',
  'sid',
  'sig',
  'signature',
  'state',
  'ticket',
  'token',
  'token_type',
  'totp',
  'verification',
  'verify',
  'xsrf',
  'xsrf_token',
  // PII (PCI / GDPR / HIPAA categories that frequently appear in URLs).
  // Aliases included so common variants (`card_number`, `routing_number`,
  // `social_security`, `birth_date`) are caught as well.
  'account',
  'account_number',
  'birth_date',
  'card',
  'cardnumber',
  'card_number',
  'credit_card',
  'cvc',
  'cvv',
  'date_of_birth',
  'dob',
  'email',
  'iban',
  'pan',
  'phone',
  'routing_number',
  'social_security',
  'ssn',
]);

/** Sentinel substituted for redacted parameter values. */
const REDACTED = '[REDACTED]';

/** Base used to parse relative URLs. The host is stripped for relative
 *  inputs so it never appears in output; only an explicit absolute origin
 *  reaches the log line.
 *  gap-fe-028-allow: legitimate URL-parse base. NOT a runtime URL the FE
 *  actually contacts — exempt from the CI guard. */
const RELATIVE_URL_BASE = 'https://razorsedge.io';

/**
 * Returns a copy of `url` with the values of any sensitive parameters
 * (per {@link SENSITIVE_QUERY_PARAMS}) replaced by `[REDACTED]` in BOTH
 * the query-string and the URL fragment.
 *
 * Behaviour:
 * - Path, benign query params, and benign fragment params are preserved.
 * - URL fragment is parsed as `URLSearchParams` when it begins with a
 *   key=value pair (OAuth implicit-flow style); otherwise the fragment
 *   is preserved verbatim.
 * - For absolute URLs whose origin differs from the app origin, the
 *   origin (`scheme://host`) is preserved in the output. Same-origin
 *   absolute URLs collapse to relative form to keep logs concise.
 * - Falsy inputs (`''`, `null`, `undefined`) return `''`.
 * - Unparseable URLs return the original string unchanged.
 *
 * @example
 *   sanitizeUrl('/auth/cb?code=abc&keep=me#section')
 *   // → '/auth/cb?code=%5BREDACTED%5D&keep=me#section'
 *
 *   sanitizeUrl('/cb#access_token=xxx&token_type=Bearer')
 *   // → '/cb#access_token=%5BREDACTED%5D&token_type=Bearer' (token_type also redacted by name)
 *
 *   sanitizeUrl('https://evil.example/cb?code=X')
 *   // → 'https://evil.example/cb?code=%5BREDACTED%5D' (origin preserved)
 */
export function sanitizeUrl(url: string | null | undefined): string {
  if (!url) return url ?? '';
  try {
    const parsed = new URL(url, RELATIVE_URL_BASE);

    // Redact query parameters.
    const sensitiveQueryKeys: string[] = [];
    parsed.searchParams.forEach((_value, key) => {
      if (SENSITIVE_QUERY_PARAMS.has(key.toLowerCase())) {
        sensitiveQueryKeys.push(key);
      }
    });
    for (const key of sensitiveQueryKeys) {
      parsed.searchParams.set(key, REDACTED);
    }

    // Redact fragment parameters when the fragment looks like
    // `#key=value&key=value` (OAuth implicit / hybrid flow). Fragments
    // that don't match (e.g. plain `#section-anchor`) are preserved.
    let sanitizedHash = parsed.hash;
    if (parsed.hash.length > 1 && parsed.hash.includes('=')) {
      const fragmentParams = new URLSearchParams(parsed.hash.slice(1));
      let mutatedFragment = false;
      const sensitiveFragmentKeys: string[] = [];
      fragmentParams.forEach((_value, key) => {
        if (SENSITIVE_QUERY_PARAMS.has(key.toLowerCase())) {
          sensitiveFragmentKeys.push(key);
        }
      });
      for (const key of sensitiveFragmentKeys) {
        fragmentParams.set(key, REDACTED);
        mutatedFragment = true;
      }
      if (mutatedFragment) {
        sanitizedHash = '#' + fragmentParams.toString();
      }
    }

    const sameOrigin = parsed.origin === RELATIVE_URL_BASE;
    const path = parsed.pathname + (parsed.search || '') + (sanitizedHash || '');
    return sameOrigin ? path : parsed.origin + path;
  } catch {
    return url;
  }
}
